import {alpha, Button, buttonClasses, Chip, Popover, styled, Typography} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {MouseEvent, useEffect, useRef, useState} from 'react';

import {FiltersDropdownItemConfig} from 'filters/features/FilterTopbar/types';
import {PageFilter} from 'filters/types';

import {useFilterLoading, useSourceFilter} from 'filters/contexts/FilterStackProvider';
import {useFilterTopbarContext} from 'filters/features/FilterTopbar/context/FilterTopbarProvider/FilterTopbarProvider';
import {shouldNotForwardProp} from 'utils/styled';
import {dayjs} from 'utils/dateUtils';
import {SaveSelectionDialog} from 'filters/features/FilterTopbar/items/FiltersDropdownItem/SaveSelectionDialog';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';
import {clearStoredId} from 'filters/utils';

import CheckIcon from 'components/icons/check.svg';
import DropdownIcon from 'components/icons/dropdown.svg';
import FiltersIcon from 'components/icons/filters.svg';
import {OverflownTooltip} from 'components/OverflownTooltip';
import LoadingIndicator from 'components/LoadingIndicator';

import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

const SChip = styled(Chip)(({theme}) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  height: theme.spacing(8),
  gap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  maxWidth: 180,

  svg: {
    flexShrink: 0,
  },
}));

const SChipLabel = styled('div')(({theme}) => ({
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const SPopover = styled(Popover)(({theme}) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.white,
    padding: theme.spacing(2, 0),
    borderRadius: theme.shape.borderRadiusRegular,
    width: 310,
    maxHeight: `min(584px, 95svh)`,
  },
}));

const SPopoverContent = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const SHr = styled('div')(({theme}) => ({
  height: 8,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SSubtitle = styled(Typography)(({theme}) => ({
  ...theme.typography.body3,
  color: theme.palette.text.secondary,
  padding: theme.spacing(2, 3),
}));

const SButton = styled(
  Button,
  shouldNotForwardProp('active')
)<{active?: boolean}>(({theme, active}) => ({
  display: 'flex',
  color: theme.palette.text.primary,
  padding: theme.spacing(2, 3),
  borderRadius: 'unset',
  justifyContent: 'start',

  ...(active && {
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  }),

  [`.${buttonClasses.endIcon}`]: {
    marginLeft: 'auto',
  },
}));

const SSavedFiltersContainer = styled(
  'div',
  shouldNotForwardProp('height')
)<{
  height: number;
}>(({height}) => ({
  height,
  overflowY: 'auto',
}));

const SRecentFilterButton = styled(SButton)(() => ({
  alignItems: 'start',
  flexDirection: 'column',
  height: 44,
  gap: 0,
}));

export const createFiltersDropdownItem = (
  config: Omit<FiltersDropdownItemConfig, 'type'>
): FiltersDropdownItemConfig => {
  return {
    ...config,
    type: 'filters-dropdown',
  };
};

export const FiltersDropdownItem = ({onClick}: FiltersDropdownItemConfig) => {
  const {useStoredFilters, useStoredFiltersActions} = useFilterActionsContext();
  const {
    savedFilters: {saved, defaultFilter},
    recentFilters,
    savedLoading,
    recentLoading,
  } = useStoredFilters();
  const {shareFilter} = useStoredFiltersActions();

  const isLoading = useFilterLoading() || savedLoading;
  const {title} = useFilterTopbarContext();
  const {useApplyFilter} = useFilterActionsContext();
  const onApply = useApplyFilter();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showMore, setShowMore] = useState(false);
  const [saveSelectionOpen, setSaveSelectionOpen] = useState(false);
  const savedFiltersRef = useRef<HTMLDivElement>(null);

  const activeFilter = useSourceFilter();
  const savedActiveFilterIndex = saved?.findIndex(f => f.id === activeFilter?.storedId) ?? -1;
  const savedActiveFilter = saved?.[savedActiveFilterIndex];
  const isDefaultFilterActive =
    defaultFilter && activeFilter ? isEqualFilters(defaultFilter, clearStoredId(activeFilter)) : false;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    if (savedActiveFilterIndex > 2) {
      setShowMore(true);
    }
  };

  useEffect(() => {
    if (showMore && savedActiveFilterIndex > 2) {
      requestAnimationFrame(() => {
        const savedFiltersElement = savedFiltersRef.current?.querySelector(`button[data-active="true"]`);

        if (savedFiltersElement) {
          savedFiltersElement.scrollIntoView({behavior: 'smooth'});
        }
      });
    }
  }, [showMore, savedActiveFilterIndex]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExited = () => {
    setShowMore(false);
  };

  const handleEditFilters = () => {
    onClick?.();
    handleClose();
  };

  const handleFilterReset = () => {
    if (!defaultFilter) {
      throw new Error('Cannot reset filters: default filter is undefined');
    }

    onApply(defaultFilter);
    handleClose();
  };

  const handleApplyFilter = (filter: PageFilter) => {
    onApply(filter);
    handleClose();
  };

  const getSavedFilters = () => {
    if (!saved?.length) {
      return (
        <SButton variant='text' size='small' disabled>
          No saved filters yet
        </SButton>
      );
    }

    const savedList = saved?.map(storedFilter => {
      const isActive = storedFilter.id === activeFilter?.storedId;

      return (
        <OverflownTooltip<HTMLButtonElement>
          title={storedFilter.name}
          key={storedFilter.id}
          enterDelay={700}
          enterNextDelay={700}
          placement='top'
        >
          {ref => (
            <SBlock>
              <SButton
                ref={ref}
                variant='text'
                size='small'
                data-active={isActive}
                active={isActive}
                endIcon={isActive ? <CheckIcon /> : null}
                onClick={!isActive ? () => handleApplyFilter(storedFilter.filter) : undefined}
              >
                <Typography variant={isActive ? 'body3Bold' : 'buttonSmall'} noWrap>
                  {storedFilter.name}
                </Typography>
              </SButton>
            </SBlock>
          )}
        </OverflownTooltip>
      );
    });

    return (
      <SSavedFiltersContainer height={Math.min(saved.length, 5) * 32} ref={savedFiltersRef}>
        {savedList}
      </SSavedFiltersContainer>
    );
  };

  const getRecentFilters = () => {
    const recent = recentFilters?.slice(1, 4);

    if (recentLoading) {
      return <LoadingIndicator size={16} padding={58} />;
    }

    if (!recent?.length) {
      return (
        <SButton variant='text' size='small' disabled>
          No recent filters yet
        </SButton>
      );
    }

    return recent.map(recentFilter => (
      <OverflownTooltip<HTMLButtonElement>
        title={recentFilter.name}
        key={recentFilter.id}
        enterDelay={700}
        enterNextDelay={700}
        placement='top'
      >
        {ref => (
          <SBlock>
            <SRecentFilterButton
              ref={ref}
              key={recentFilter.id}
              variant='text'
              size='small'
              onClick={() => handleApplyFilter(recentFilter.filter)}
            >
              <Typography variant='buttonSmall' noWrap width='100%' textAlign='left'>
                {recentFilter.name}
              </Typography>

              <Typography variant='pillNumber' color='text.secondary'>
                {dayjs(recentFilter.lastUsed).fromNow()}
              </Typography>
            </SRecentFilterButton>
          </SBlock>
        )}
      </OverflownTooltip>
    ));
  };

  const filterName = savedActiveFilter ? savedActiveFilter.name : isDefaultFilterActive ? 'Default' : 'Filters';

  return (
    <>
      <OverflownTooltip title={filterName}>
        {ref => (
          <SChip
            disabled={isLoading}
            data-testid='edit-filters-chip'
            // We need to pass storedId from sourceFilter to identify the filter in e2e tests
            data-stored-id={activeFilter?.storedId}
            onClick={handleClick}
            icon={isLoading ? <CircularProgress size={16} /> : <FiltersIcon width={16} color='text.primary' />}
            label={
              <SChipLabel>
                <Typography noWrap ref={ref} variant='body3'>
                  {filterName}
                </Typography>

                <DropdownIcon />
              </SChipLabel>
            }
          />
        )}
      </OverflownTooltip>

      <SPopover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionProps={{
          onExited: handleExited,
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              marginTop: 2,
            },
          },
        }}
      >
        <SPopoverContent>
          <Typography variant='subtitle2' textAlign='center' padding={1}>
            {title}
          </Typography>

          <SBlock>
            <SSubtitle>Active</SSubtitle>

            <SButton variant='text' size='small' data-testid='topbar-edit-filters-button' onClick={handleEditFilters}>
              Edit filters
            </SButton>

            <SButton
              variant='text'
              size='small'
              disabled={isDefaultFilterActive || savedActiveFilterIndex !== -1}
              onClick={() => {
                setSaveSelectionOpen(true);
                handleClose();
              }}
            >
              Save selection as...
            </SButton>

            {shareFilter && (
              <SButton
                variant='text'
                size='small'
                disabled={isDefaultFilterActive}
                onClick={() => {
                  const activeFilterId = recentFilters?.[0].id;

                  if (!activeFilterId) {
                    throw new Error('Cannot share filters');
                  }

                  void shareFilter(activeFilterId);
                  handleClose();
                }}
                data-testid='topbar-share-filters-button'
              >
                Share filters
              </SButton>
            )}

            {defaultFilter && (
              <SButton
                variant='text'
                size='small'
                onClick={handleFilterReset}
                data-testid='topbar-reset-filters-button'
                disabled={isDefaultFilterActive}
              >
                Reset to default
              </SButton>
            )}
          </SBlock>

          <SBlock>
            <SHr />
            <SSubtitle>Saved ({saved?.length || 0})</SSubtitle>
            {getSavedFilters()}
          </SBlock>

          {recentFilters !== null && (
            <SBlock>
              <SHr />
              <SSubtitle>Recent</SSubtitle>
              {getRecentFilters()}
            </SBlock>
          )}
        </SPopoverContent>
      </SPopover>

      <SaveSelectionDialog
        open={saveSelectionOpen}
        onClose={() => setSaveSelectionOpen(false)}
        useStoredFilters={useStoredFilters}
        useStoredFiltersActions={useStoredFiltersActions}
      />
    </>
  );
};
